<template>
    <div>
        <div class="cal-controls" ref="controls" :style="{ top: controlsTop, paddingTop: controlsPadTop }">
            <div class="segmented">
                <button v-for="v in views" :key="v" :class="['segmented-item', { active: view === v }]" @click="view = v">
                    {{ $t(v) }}
                </button>
            </div>

            <div class="cal-toolbar">
                <button class="cal-nav-btn" @click="move(-1)"><i class="fa-solid fa-chevron-left"></i></button>
                <div class="cal-title">{{ title }}</div>
                <button class="cal-nav-btn" @click="move(1)"><i class="fa-solid fa-chevron-right"></i></button>
            </div>
            <button v-if="!isToday" class="cal-today-btn" @click="goToday">{{ $t('today') }}</button>
        </div>

        <!-- DAY -->
        <template v-if="view === 'day'">
            <div v-if="dayEvents.length === 0" class="empty-state">
                <i class="fa-regular fa-calendar"></i>
                {{ $t('noSessionsThisDay') }}
            </div>
            <SessionCard v-for="event in dayEvents" :key="event.id" :event="event" @open="openEvent" />
        </template>

        <!-- WEEK -->
        <template v-else-if="view === 'week'">
            <div v-for="day in weekDays" :key="day.date">
                <div :class="['day-heading', { 'is-today': day.isToday }]">{{ day.label }}</div>
                <SessionCard v-for="event in day.events" :key="event.id" :event="event" @open="openEvent" />
                <div v-if="day.events.length === 0" class="session-meta" style="padding: 2px 4px 6px;">
                    {{ $t('noSessions') }}
                </div>
            </div>
        </template>

        <!-- MONTH -->
        <template v-else>
            <div class="month-grid">
                <div v-for="dow in monthDows" :key="dow" class="month-dow">{{ dow }}</div>
                <button v-for="cell in monthCells" :key="cell.date"
                        :class="['month-cell', { outside: cell.outside, 'is-today': cell.isToday, 'has-signed': cell.hasSigned }]"
                        @click="openDay(cell.date)">
                    <span>{{ cell.day }}</span>
                    <span v-if="cell.count > 0" class="month-count">{{ cell.count }}</span>
                </button>
            </div>
        </template>

        <SessionSheet :event="selectedEvent" @close="selectedEvent = null" @signup="signup" @cancel="cancel"
                      @waitlist-join="waitlistJoin" @waitlist-leave="waitlistLeave" />
    </div>
</template>

<script>
import moment from 'moment';
import SessionCard from '@/components/SessionCard.vue';
import SessionSheet from '@/components/SessionSheet.vue';

export default {
    name: 'CalendarView',
    components: { SessionCard, SessionSheet },
    data() {
        return {
            views: ['day', 'week', 'month'],
            view: 'week',
            cursor: moment().format('YYYY-MM-DD'),
            events: [],
            selectedEvent: null,
            // Sticky offset for the calendar controls: the header's height on phones (where the page scrolls
            // under a sticky header), 0 on the desktop grid (where .app-main scrolls beneath a static header).
            controlsTop: '0px',
            // The top gap normally provided by .app-main's padding, moved inside the sticky bar so it survives
            // pinning (no snap) and its cream background covers content scrolling underneath.
            controlsPadTop: '0px'
        };
    },
    computed: {
        today() {
            return moment().format('YYYY-MM-DD');
        },
        isToday() {
            return this.cursor === this.today;
        },
        range() {
            const m = moment(this.cursor, 'YYYY-MM-DD');
            if (this.view === 'day') {
                return { start: this.cursor, end: this.cursor };
            }
            if (this.view === 'week') {
                return {
                    start: m.clone().startOf('isoWeek').format('YYYY-MM-DD'),
                    end: m.clone().endOf('isoWeek').format('YYYY-MM-DD')
                };
            }
            return {
                start: m.clone().startOf('month').startOf('isoWeek').format('YYYY-MM-DD'),
                end: m.clone().endOf('month').endOf('isoWeek').format('YYYY-MM-DD')
            };
        },
        title() {
            // .locale(store language) both localises the labels and makes this recompute when it changes.
            const m = moment(this.cursor, 'YYYY-MM-DD').locale(this.$store.state.language);
            if (this.view === 'day') return m.format('dddd, D. MMMM');
            if (this.view === 'week') {
                const start = m.clone().startOf('isoWeek');
                const end = m.clone().endOf('isoWeek');
                return start.format('D.M.') + ' – ' + end.format('D.M.YYYY.');
            }
            return m.format('MMMM YYYY.');
        },
        eventsByDate() {
            const map = {};
            for (const event of this.events) {
                const date = event.start.substring(0, 10);
                if (!map[date]) map[date] = [];
                map[date].push(event);
            }
            for (const date in map) {
                map[date].sort((a, b) => a.start.localeCompare(b.start));
            }
            return map;
        },
        dayEvents() {
            return this.eventsByDate[this.cursor] || [];
        },
        weekDays() {
            const start = moment(this.cursor, 'YYYY-MM-DD').locale(this.$store.state.language).startOf('isoWeek');
            const days = [];
            for (let i = 0; i < 7; i++) {
                const d = start.clone().add(i, 'days');
                const date = d.format('YYYY-MM-DD');
                days.push({
                    date,
                    label: d.format('dddd, D.M.'),
                    isToday: date === this.today,
                    events: this.eventsByDate[date] || []
                });
            }
            return days;
        },
        monthDows() {
            const s = this.$strings.weekdaysShort;
            return [s[1], s[2], s[3], s[4], s[5], s[6], s[0]];
        },
        monthCells() {
            const m = moment(this.cursor, 'YYYY-MM-DD');
            const month = m.month();
            const cursorDate = m.clone().startOf('month').startOf('isoWeek');
            const end = m.clone().endOf('month').endOf('isoWeek');
            const cells = [];
            const iter = cursorDate.clone();
            while (iter.isSameOrBefore(end, 'day')) {
                const date = iter.format('YYYY-MM-DD');
                const dayEvents = this.eventsByDate[date] || [];
                cells.push({
                    date,
                    day: iter.date(),
                    outside: iter.month() !== month,
                    isToday: date === this.today,
                    count: dayEvents.length,
                    hasSigned: dayEvents.some((e) => e.signed_up)
                });
                iter.add(1, 'days');
            }
            return cells;
        }
    },
    watch: {
        range: {
            handler(newRange, oldRange) {
                if (!oldRange || newRange.start !== oldRange.start || newRange.end !== oldRange.end) {
                    this.load();
                }
            },
            immediate: true
        }
    },
    methods: {
        async load() {
            const response = await this.$api.get('sessions/calendar', this.range);
            if (response.success) {
                this.events = response.data.events;
                this.scrollToFirstUpcoming();
            }
        },
        scrollToFirstUpcoming() {
            // Day & week views: bring the first session that hasn't happened yet to the top of the scroll area,
            // so members land on what's next rather than on the morning's already-finished sessions. Past
            // sessions carry the `is-past` class, so the first card without it is the first upcoming one. If it
            // is far down, earlier (expired) sessions naturally stay visible above it. When nothing is upcoming
            // (a fully past day/week) or there are no cards (month view) we just start from the top.
            if (this.view !== 'day' && this.view !== 'week') return;
            // Two frames: let Vue patch the DOM, then let the browser lay it out before measuring positions.
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    if (!this.$el) return;
                    this.updateControlsTop();
                    // The scroller is the window on phones (the whole page scrolls) but .app-main on the desktop
                    // grid layout, so find the real one instead of assuming.
                    const scroller = this.getScrollParent(this.$el);
                    const card = this.$el.querySelector('.session-card:not(.is-past)');
                    const gap = 8;
                    // The controls are pinned at the top of the scroll area, so the card must clear their height.
                    const controls = this.$refs.controls;
                    const controlsH = controls ? controls.getBoundingClientRect().height : 0;

                    if (!card) {
                        if (scroller) scroller.scrollTop = 0;
                        else window.scrollTo(0, 0);
                        return;
                    }

                    if (scroller) {
                        // On desktop the header is outside the scroller; only the sticky controls occlude the top.
                        const delta = card.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
                        scroller.scrollTop = Math.max(0, scroller.scrollTop + delta - controlsH - gap);
                    } else {
                        // Page scroll: clear both the sticky header and the sticky controls stacked below it.
                        const header = document.querySelector('.app-header');
                        const headerH = header ? header.getBoundingClientRect().height : 0;
                        const y = window.scrollY + card.getBoundingClientRect().top - headerH - controlsH - gap;
                        window.scrollTo(0, Math.max(0, y));
                    }
                });
            });
        },
        updateControlsTop() {
            // Move .app-main's top padding into the sticky bar. Read the stylesheet's intended value for the
            // current breakpoint (16px phone / 32px desktop) by briefly clearing our inline override, then zero
            // the real padding so the bar sits at the container's top edge and re-adds that gap as its own
            // padding-top. Result: the gap above the controls is identical whether resting or pinned - no snap.
            const appMain = this.appMainEl;
            let gap = 0;
            if (appMain) {
                appMain.style.paddingTop = '';
                gap = parseFloat(getComputedStyle(appMain).paddingTop) || 0;
                appMain.style.paddingTop = '0px';
            }
            this.controlsPadTop = gap + 'px';

            const header = document.querySelector('.app-header');
            // The header is sticky (overlays the viewport top) on phones and static (its own grid row) on
            // desktop; only in the sticky case must the controls sit below it.
            if (header && getComputedStyle(header).position === 'sticky') {
                this.controlsTop = Math.round(header.getBoundingClientRect().height) + 'px';
            } else {
                this.controlsTop = '0px';
            }
        },
        getScrollParent(el) {
            // Nearest ancestor that actually scrolls vertically; null means the page/window is the scroller.
            let node = el.parentElement;
            while (node && node !== document.body && node !== document.documentElement) {
                const overflowY = getComputedStyle(node).overflowY;
                if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
                    return node;
                }
                node = node.parentElement;
            }
            return null;
        },
        move(direction) {
            const unit = this.view === 'day' ? 'days' : this.view === 'week' ? 'weeks' : 'months';
            this.cursor = moment(this.cursor, 'YYYY-MM-DD').add(direction, unit).format('YYYY-MM-DD');
        },
        goToday() {
            this.cursor = this.today;
        },
        openDay(date) {
            this.cursor = date;
            this.view = 'day';
        },
        openEvent(event) {
            this.selectedEvent = event;
        },
        applyStatus(status) {
            const event = this.events.find((e) => e.id === status.session_id);
            if (!event) return;
            event.attendance = status.attendance;
            event.signed_up = status.signed_up;
            event.on_waiting_list = status.on_waiting_list;
            event.waiting_position = status.waiting_position;
            event.waiting_count = status.waiting_count;
            event.full = event.capacity > 0 && event.attendance >= event.capacity;
        },
        async signup(event) {
            const response = await this.$api.post('sessions/signup', { session_id: event.id });
            if (response.success) {
                this.applyStatus(response.data.status);
                this.selectedEvent = null;
                this.$store.commit('showToast', { message: this.$t('signupSuccess'), type: 'success' });
            } else {
                this.handleSignupError(response.data);
                this.load();
            }
        },
        async cancel(event) {
            const response = await this.$api.post('sessions/cancel', { session_id: event.id });
            if (response.success) {
                this.applyStatus(response.data.status);
                this.selectedEvent = null;
                this.$store.commit('showToast', { message: this.$t('cancelSuccess'), type: 'success' });
            } else {
                this.$store.commit('showToast', { message: this.$t('genericError'), type: 'error' });
            }
        },
        async waitlistJoin(event) {
            const response = await this.$api.post('sessions/waitlist/join', { session_id: event.id });
            if (response.success) {
                // Keep the sheet open so they see their position and can leave again.
                this.applyStatus(response.data.status);
                this.$store.commit('showToast', { message: this.$t('joinWaitlistSuccess'), type: 'success' });
            } else {
                this.handleSignupError(response.data);
                this.load();
            }
        },
        async waitlistLeave(event) {
            const response = await this.$api.post('sessions/waitlist/leave', { session_id: event.id });
            if (response.success) {
                this.applyStatus(response.data.status);
                this.$store.commit('showToast', { message: this.$t('leaveWaitlistSuccess'), type: 'success' });
            } else {
                this.$store.commit('showToast', { message: this.$t('genericError'), type: 'error' });
            }
        },
        handleSignupError(data) {
            const map = {
                membership_required: 'errorMembershipRequired',
                capacity_full: 'errorCapacityFull',
                session_passed: 'errorSessionPassed',
                registration_closed: 'errorRegistrationClosed'
            };
            const key = data && map[data.error] ? map[data.error] : 'genericError';
            this.selectedEvent = null;
            this.$store.commit('showToast', { message: this.$t(key), type: 'error' });
        }
    },
    mounted() {
        this.appMainEl = this.$el.closest('.app-main');
        this.updateControlsTop();
        this.onResize = () => this.updateControlsTop();
        window.addEventListener('resize', this.onResize);
        window.addEventListener('orientationchange', this.onResize);
    },
    unmounted() {
        // Hand .app-main's top padding back so other views keep their normal top spacing.
        if (this.appMainEl) this.appMainEl.style.paddingTop = '';
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('orientationchange', this.onResize);
    }
};
</script>
